/**
 * Sign-out control — enforces the mandated sign-out ordering (§11.5):
 *
 *   1. await api.auth.deleteSession()  — DELETE /api/v1/auth/session/ (204)
 *      Records auth_user_signed_out audit event + broadcast BEFORE the
 *      token is invalidated. MUST complete while the user is authenticated.
 *   2. websocketManager.disconnect()  — close the socket gracefully.
 *   3. instance.logoutRedirect()      — clears the MSAL sessionStorage cache
 *      and navigates to the Entra sign-out endpoint.
 *
 * If the DELETE fails (network error) the failure is logged client-side and
 * logout proceeds anyway (best-effort audit).
 */

import React, { useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { api } from '@/services/api'
import { websocketManager } from '@/services/websocketCompat'

interface SignOutButtonProps {
  /** Optional className for the outer element. */
  className?: string
  /** Optional label override (defaults to "Sign out"). */
  label?: string
}

/**
 * Button that signs the user out following the mandated sign-out ordering.
 *
 * Role-based write-control disabling lives at the individual write-control
 * level, not here. This component handles auth lifecycle only.
 */
const SignOutButton: React.FC<SignOutButtonProps> = ({
  className,
  label = 'Sign out',
}) => {
  const { instance } = useMsal()
  const [isSigningOut, setIsSigningOut] = useState(false)

  const handleSignOut = async (): Promise<void> => {
    if (isSigningOut) return
    setIsSigningOut(true)

    // Step 1 — audit the sign-out on the backend while the token is still
    // valid. If this fails (network), log and proceed (best-effort audit).
    try {
      await api.auth.deleteSession()
    } catch (err) {
      console.warn('Sign-out: DELETE /api/v1/auth/session/ failed (best-effort):', err)
    }

    // Step 2 — disconnect the WebSocket gracefully.
    websocketManager.disconnect()

    // Step 3 — clear the MSAL sessionStorage cache and redirect to Entra
    // sign-out. After this the access token is gone; any further API call
    // would 401 (which is why DELETE must precede this step).
    await instance.logoutRedirect().catch((err) => {
      console.error('Sign-out: logoutRedirect failed:', err)
      setIsSigningOut(false)
    })
  }

  return (
    <button
      type="button"
      onClick={handleSignOut}
      disabled={isSigningOut}
      className={className}
      aria-label={isSigningOut ? 'Signing out…' : label}
    >
      {isSigningOut ? 'Signing out…' : label}
    </button>
  )
}

SignOutButton.displayName = 'SignOutButton'

export default SignOutButton
